import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Label from 'LuxKit/Label'
import AspectRatio from 'components/utils/AspectRatio'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import LayoutContainer from 'components/Common/LayoutContainer'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { LOYALTY_LANDING_PAGE, LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'

const HERO_IMAGE_ID = 'societe-rewards-landing-hero-v2'

const HeroSection = styled.div`
  background-color: ${props => props.theme.palette.neutral.control.black100};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  padding: ${rem(64)} 0;
`

const ImageContainer = styled(AspectRatio)`
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
`

const HeroLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};

  @media (min-width: 704px) {
    flex-direction: row;
    align-items: center;
    gap: ${rem(40)};
  }
`

function LoyaltyRewardsHeroSection() {
  const intl = useIntl()

  const allBenefitsLabel = intl.formatMessage({
    id: 'nSkdUZGZGiPGthh4mvh2',
    defaultMessage: 'All benefits',
    description: 'Back link to all Société benefits from the Société rewards page',
  })

  const newBadgeLabel = intl.formatMessage({
    id: 'i5PtFTyhgCDZE2duLqxU',
    defaultMessage: 'New',
    description: 'Small badge label in the hero of the Société rewards page',
  })

  const heading = intl.formatMessage({
    id: 'HXxkpWE8ljR_uadK6Sbg',
    defaultMessage: 'Société Rewards',
    description: 'Main hero heading displayed at the top of the Société rewards page',
  })

  const subheading = intl.formatMessage({
    id: 'u5zX6YHpTJW5K4U6Gd8u',
    defaultMessage: 'Book your dream Maldives holiday with Société Points.',
    description: 'Hero subheading on the Société rewards page explaining the benefits of the Société rewards program',
  })

  const body = intl.formatMessage({
    id: '9t382lKlimeUmM3OhwTP',
    defaultMessage: 'Reward Seats are now available exclusively on our direct Maldives return flights, at a redemption rate that beats booking at full price.',
    description: 'Hero body copy on the Société rewards page explaining reward seat availability',
  })

  const browseBundlesLabel = intl.formatMessage({
    id: 'r7KgP92evb8F8DTe3zSF',
    defaultMessage: 'Browse Maldives Bundles',
    description: 'Primary call-to-action button in the Société rewards page hero',
  })

  const topUpPointsLabel = intl.formatMessage({
    id: 'vqmeuR9NT_sBVI8nLYoz',
    defaultMessage: 'Top up points',
    description: 'Secondary call-to-action link in the Société rewards page hero',
  })

  return <HeroSection>
    <LayoutContainer variant="medium">
      <HeroLayout>
        <VerticalSpacer gap={40}>
          <ClickableLink to={`/${LOYALTY_LANDING_PAGE}#benefits`}>
            <BodyTextBlock variant="medium" startIcon={<LineAngleLeftIcon />} colour="dark-contrast">
              {allBenefitsLabel}
            </BodyTextBlock>
          </ClickableLink>
          <VerticalSpacer gap={20}>
            <VerticalSpacer gap={12}>
              <Label kind="primary" variant="highlight">
                {newBadgeLabel}
              </Label>
              <Heading variant="heading1" as="h1">
                {heading}
              </Heading>
              <Heading variant="heading3">
                {subheading}
              </Heading>
              <BodyText variant="large">
                {body}
              </BodyText>
            </VerticalSpacer>
            <Group direction="vertical" tabletDirection="horizontal" gap={8} verticalAlign="center" horizontalAlign="start">
              <TextButton kind="secondary" variant="dark" size="large" to={`/campaign/${CHARTER_PAGE_SLUG}`} fit="mobile-full-width">
                {browseBundlesLabel}
              </TextButton>
              <TextButton kind="tertiary" variant="ghost" size="large" to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`} fit="mobile-full-width">
                {topUpPointsLabel}
              </TextButton>
            </Group>
          </VerticalSpacer>
        </VerticalSpacer>
        <ImageContainer ratio="433/350" width={433}>
          <Image
            id={HERO_IMAGE_ID}
            alt=""
            fit="center"
            loading="eager"
            dpr={2}
            width={433}
            height={350}
          />
        </ImageContainer>
      </HeroLayout>
    </LayoutContainer>
  </HeroSection>
}

export default LoyaltyRewardsHeroSection
